import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { Mail, Workflow } from "lucide-react";
import { usePlaybooks, type Person, type Playbook } from "../../lib/admin";
import { Ago, Button, Card, CardEmpty, CardList, CardRow, ErrorState, PageHeader, Skeleton, cx, useShowMore } from "../../components/ui";
import { usePageTitle, ComposeModal, EnrollModal, Health, LifecycleBadge, StageBadge } from "./components";

const SHOWN = 6;

/** Rule-based plays: who to talk to, why, and a one-click way to act on the whole list. */
export function AdminPlays() {
  const q = usePlaybooks();
  usePageTitle("Plays");
  const { hash } = useLocation();
  const [compose, setCompose] = useState<{ person: Person; template: string } | null>(null);
  const [enroll, setEnroll] = useState<string[] | null>(null);

  useEffect(() => {
    if (!hash || !q.data) return;
    // Move focus, not just the viewport, so keyboard and screen-reader users land on the play.
    const el = document.getElementById(hash.slice(1));
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    el.focus({ preventScroll: true });
  }, [hash, q.data]);

  return (
    <>
      <PageHeader title="Plays" subtitle="Segments computed from live usage, each with the move that tends to work. Refreshes on every visit." />
      <div className="space-y-4">
        {q.isError && (
          <Card>
            <ErrorState error={q.error} />
          </Card>
        )}
        {q.isPending && Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-40" />)}
        {q.data?.map((pb) => (
          <PlayCard
            key={pb.id}
            pb={pb}
            targeted={hash === `#${pb.id}`}
            onCompose={(person) => setCompose({ person, template: pb.template })}
            onEnroll={setEnroll}
          />
        ))}
      </div>
      {compose && <ComposeModal open onOpenChange={(o) => !o && setCompose(null)} contact={compose.person} defaultTemplate={compose.template} />}
      {enroll && <EnrollModal open onOpenChange={(o) => !o && setEnroll(null)} contactIds={enroll} />}
    </>
  );
}

function PlayCard({
  pb,
  targeted,
  onCompose,
  onEnroll,
}: {
  pb: Playbook & { people: Person[] };
  targeted: boolean;
  onCompose: (person: Person) => void;
  onEnroll: (ids: string[]) => void;
}) {
  const reachable = pb.people.filter((p) => !p.unsubscribed);
  const more = useShowMore(pb.people, SHOWN, "people");
  return (
    // The card is the #hash target: focusable from script, named by its heading.
    <Card id={pb.id} tabIndex={-1} aria-labelledby={`${pb.id}-title`} className={cx("scroll-mt-4 outline-none", targeted && "ring-2 ring-accent-wash")}>
      <header className="flex flex-wrap items-start justify-between gap-3 px-4 pt-4 pb-3">
        <div className="min-w-0 max-w-2xl">
          <h2 id={`${pb.id}-title`} className="flex items-center gap-2 text-sm font-semibold text-ink">
            {pb.name}
            <span className="rounded-md bg-surface-2 px-1.5 py-0.5 text-[11px] font-medium text-ink-2 tabular">
              {pb.people.length}
              <span className="sr-only"> {pb.people.length === 1 ? "person" : "people"}</span>
            </span>
          </h2>
          <p className="mt-0.5 text-xs text-ink-3">{pb.why}</p>
          <p className="mt-1.5 text-[13px] text-ink-2">
            <span className="font-medium text-ink">Play:</span> {pb.play} <span className="text-ink-3">Template: {pb.template}.</span>
          </p>
        </div>
        <Button size="sm" variant="primary" disabled={!reachable.length} onClick={() => onEnroll(reachable.map((p) => p.id))} aria-label={`Add ${reachable.length} people from ${pb.name} to a sequence`}>
          <Workflow size={13} /> Add {reachable.length} to sequence
        </Button>
      </header>
      {pb.people.length ? (
        <CardList id={more.listId}>
          {more.visible.map((p) => (
            <CardRow key={p.id} wrap>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Link to={`/admin/people/${p.id}`} className="truncate text-sm font-medium text-ink hover:underline">
                    {p.name ?? p.email}
                  </Link>
                  <StageBadge value={p.stage} />
                </div>
                <div className="truncate text-xs text-ink-3">
                  {p.email}
                  {p.account && <> · {p.account.name}</>} · contacted <Ago t={p.lastContactedAt} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                {p.account && (
                  <>
                    <LifecycleBadge value={p.account.lifecycle} />
                    <Health score={p.account.health} compact />
                  </>
                )}
                <Button size="sm" onClick={() => onCompose(p)} disabled={p.unsubscribed} aria-label={`Email ${p.name ?? p.email}`}>
                  <Mail size={13} /> Email
                </Button>
              </div>
            </CardRow>
          ))}
        </CardList>
      ) : (
        <CardEmpty divided>Nobody right now.</CardEmpty>
      )}
      {more.toggle && <div className="border-t border-line px-4 py-2">{more.toggle}</div>}
    </Card>
  );
}
